import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Add, Remove } from "@mui/icons-material";
import { useCartOperationStatus } from "../../../../hooks/useCartOperationStatus";
import ProductImage from "../../../../components/common/ProductImage/ProductImage";
import {
  calculateBasePrice,
  calculateOptionsSurcharge,
} from "../../../../utils/cartCalculationUtils";
import styles from "./CartItem.module.css";

interface CartItemProps {
  item: any;
  handleCartAmountChange: (quantity: number, item: any) => void;
  handleRemoveItem: (item: any) => void;
  onDeliveryDataChange?: (cartCustomId: string, data: any) => void;
  stockValidation?: any;
  allowRemoveOnOutOfStock?: boolean;
  operationInProgress?: boolean;
  optimisticQuantity?: number | null;
}

const CartItem: React.FC<CartItemProps> = ({
  item,
  handleCartAmountChange,
  handleRemoveItem,
  onDeliveryDataChange,
  stockValidation,
  allowRemoveOnOutOfStock = false,
  operationInProgress: forcedOperation = false,
  optimisticQuantity: externalOptimisticQuantity = null,
}) => {
  const [localOptimisticQuantity, setLocalOptimisticQuantity] = useState<
    number | null
  >(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [localOperationType, setLocalOperationType] = useState<string | null>(
    null
  );
  const [showOptimisticPrice, setShowOptimisticPrice] = useState(false);

  const groupId =
    item.groupId ||
    (item.group && typeof item.group === "object" ? item.group.id : null);

  const { isLoading: itemOperationInProgress, operationType } =
    useCartOperationStatus(groupId, item.cartCustomId);

  const isOperationInProgress = itemOperationInProgress || forcedOperation;

  const hasStockIssue = stockValidation?.isValid === false;
  const availableStock = hasStockIssue
    ? stockValidation.availableStock
    : item.product?.stock || 0;

  const optimisticQuantity =
    externalOptimisticQuantity !== null
      ? externalOptimisticQuantity
      : localOptimisticQuantity;

  const basePrice = calculateBasePrice(item);
  const optionSurcharge = calculateOptionsSurcharge(item);
  const quantity =
    optimisticQuantity !== null ? optimisticQuantity : item.quantity;
  const totalPrice = (basePrice + optionSurcharge) * quantity;

  const prevQuantityRef = useRef(item.quantity);

  useEffect(() => {
    if (!isOperationInProgress && (localOperationType || showOptimisticPrice)) {
      const timer = setTimeout(() => {
        setLocalOperationType(null);
        setShowOptimisticPrice(false);
        if (externalOptimisticQuantity === null) {
          setLocalOptimisticQuantity(null);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [
    isOperationInProgress,
    localOperationType,
    showOptimisticPrice,
    externalOptimisticQuantity,
  ]);

  useEffect(() => {
    if (item.quantity !== prevQuantityRef.current) {
      prevQuantityRef.current = item.quantity;
      setShowOptimisticPrice(false);
      if (externalOptimisticQuantity === null) {
        setLocalOptimisticQuantity(null);
      }
      setLocalOperationType(null);
    }
  }, [item.quantity, externalOptimisticQuantity]);

  useEffect(() => {
    if (externalOptimisticQuantity === null) {
      setLocalOptimisticQuantity(null);
    }
  }, [item.quantity, externalOptimisticQuantity]);

  const handleDecrease = () => {
    if (isOperationInProgress && localOperationType === "ITEM_REMOVAL") return;

    const newQuantity = quantity - 1;

    if (newQuantity <= 0) {
      setIsRemoving(true);
      setLocalOperationType("ITEM_REMOVAL");
      if (externalOptimisticQuantity === null) {
        setLocalOptimisticQuantity(0);
      }
      handleRemoveItem(item);
    } else {
      setLocalOperationType("QUANTITY_UPDATE");
      setShowOptimisticPrice(true);
      if (externalOptimisticQuantity === null) {
        setLocalOptimisticQuantity(newQuantity);
      }
      handleCartAmountChange(newQuantity, item);
    }
  };

  const handleIncrease = () => {
    if (isOperationInProgress && localOperationType === "ITEM_REMOVAL") return;
    if (quantity >= availableStock) return;

    const newQuantity = quantity + 1;
    setLocalOperationType("QUANTITY_UPDATE");
    setShowOptimisticPrice(true);
    if (externalOptimisticQuantity === null) {
      setLocalOptimisticQuantity(newQuantity);
    }
    handleCartAmountChange(newQuantity, item);
  };

  const handleRemove = () => {
    if (isOperationInProgress && localOperationType === "ITEM_REMOVAL") return;

    setIsRemoving(true);
    setLocalOperationType("ITEM_REMOVAL");
    setShowOptimisticPrice(true);
    if (externalOptimisticQuantity === null) {
      setLocalOptimisticQuantity(0);
    }
    handleRemoveItem(item);
  };

  if (quantity <= 0 && isRemoving) return null;

  const itemClasses = `${styles.cartItem} 
    ${hasStockIssue ? styles.outOfStock : ""} 
    ${isOperationInProgress ? styles.updating : ""}
    ${localOperationType === "QUANTITY_UPDATE" ? styles.quantityUpdating : ""}
    ${localOperationType === "ITEM_REMOVAL" ? styles.removing : ""}`;

  return (
    <div
      className={itemClasses}
      data-testid="cart-item"
      data-group-id={groupId}
      data-cart-custom-id={item.cartCustomId}
    >
      {isOperationInProgress && localOperationType === "ITEM_REMOVAL" && (
        <div className={styles.imageOverlayStatus}>
          <span className={styles.miniSpinner}></span>
          <span className={styles.statusTextRemoval}>Eliminando</span>
        </div>
      )}

      <Link
        to={`/p/${item.product?.url || ""}`}
        className={`${styles.imageContainer} ${
          isOperationInProgress && localOperationType === "ITEM_REMOVAL"
            ? styles.imageUpdating
            : ""
        }`}
      >
        <ProductImage
          src={item.product?.images?.[0]?.small || ""}
          alt={item.product?.name || "Product"}
          className={styles.productImage}
        />
      </Link>

      <div className={styles.mainContent}>
        <Link
          to={`/p/${item.product?.url || ""}`}
          className={styles.productName}
        >
          {item.product?.name || "Product"}
        </Link>

        {(item.customizableOptions?.length > 0 ||
          item.groupingAttributes?.length > 0) && (
          <div className={styles.productOptions}>
            {item.groupingAttributes?.map((attr: any, index: number) => (
              <span key={`attr-${index}`} className={styles.optionItem}>
                {attr.name}: <strong>{attr.value}</strong>
              </span>
            ))}

            {item.customizableOptions?.map((option: any, index: number) => (
              <span key={`option-${index}`} className={styles.optionItem}>
                {option.label}:{" "}
                <strong>
                  {Array.isArray(option.value)
                    ? option.value.join(", ")
                    : option.value}
                </strong>
              </span>
            ))}
          </div>
        )}

        <div className={styles.priceBlock}>
          <span className={styles.unitPrice}>
            S/ {basePrice.toFixed(2)}
            {optionSurcharge > 0 && ` + S/ ${optionSurcharge.toFixed(2)}`} c/u
          </span>
          <span
            className={`${styles.totalPrice} ${
              optimisticQuantity !== null && showOptimisticPrice
                ? styles.optimisticPrice
                : ""
            }`}
          >
            S/ {totalPrice.toFixed(2)}
          </span>
        </div>
      </div>

      {hasStockIssue && (
        <div className={styles.stockWarning}>
          <span className={styles.stockWarningText}>
            {stockValidation.availableStock === 0
              ? "Sin stock"
              : `Solo ${stockValidation.availableStock} disponible${
                  stockValidation.availableStock > 1 ? "s" : ""
                }`}
          </span>
        </div>
      )}

      <div className={styles.actions}>
        <div className={styles.qtyControls}>
          <button
            className={styles.qtyButton}
            onClick={handleDecrease}
            disabled={localOperationType === "ITEM_REMOVAL"}
            aria-label="Decrease quantity"
          >
            <Remove fontSize="small" />
          </button>

          <span
            className={`${styles.qtyText} ${
              optimisticQuantity !== null &&
              optimisticQuantity !== item.quantity &&
              showOptimisticPrice
                ? styles.optimisticQty
                : ""
            }`}
          >
            {quantity}
          </span>

          <button
            className={styles.qtyButton}
            onClick={handleIncrease}
            disabled={
              localOperationType === "ITEM_REMOVAL" ||
              quantity >= availableStock
            }
            aria-label="Increase quantity"
          >
            <Add fontSize="small" />
          </button>
        </div>

        <button
          className={`${styles.removeButton} ${
            hasStockIssue ? styles.outOfStockRemoveButton : ""
          }`}
          onClick={handleRemove}
          disabled={
            isOperationInProgress && localOperationType === "ITEM_REMOVAL"
          }
        >
          Eliminar
        </button>
      </div>
    </div>
  );
};

export default CartItem;
